import type { Block } from '@datum/shared'
import { parseBlocks } from '@datum/shared'
import { Check, Image as ImageIcon, Mic } from 'lucide-react'
import { useState } from 'react'
import { useAttachmentUrl } from '../lib/attachments'

interface NoteViewerProps {
  contentRaw: string
  /** When provided, todo checkboxes become interactive. */
  onToggleTodo?: (todoIndex: number) => void
  compact?: boolean
}

function AttachmentChip({ block }: { block: Extract<Block, { type: 'attachment' }> }) {
  const Icon = block.kind === 'audio' ? Mic : ImageIcon
  return (
    <span className="mono inline-flex items-center gap-1.5 rounded border border-edge bg-ink-800 px-2 py-0.5 text-[11px] text-zinc-300">
      <Icon size={12} className="text-zinc-500" />
      {block.kind}
      <span className="text-zinc-500">{block.attachmentId ? `:${block.attachmentId.slice(0, 8)}` : ':uploading…'}</span>
    </span>
  )
}

/**
 * Signed URLs come from the API with a 15-min TTL (attachments.ts caches with
 * margin). Until one resolves — or if signing fails/offline — the chip
 * fallback keeps the block visible with its id.
 */
function AttachmentView({ block }: { block: Extract<Block, { type: 'attachment' }> }) {
  const url = useAttachmentUrl(block.attachmentId)
  const [failed, setFailed] = useState(false)
  if (block.attachmentId === undefined || block.attachmentId === '' || url === null || failed) {
    return <AttachmentChip block={block} />
  }
  if (block.kind === 'photo') {
    return (
      <a href={url} target="_blank" rel="noreferrer" title="Open full size" className="w-fit">
        <img
          src={url}
          alt={`attached photo ${block.attachmentId.slice(0, 8)}`}
          loading="lazy"
          onError={() => setFailed(true)}
          className="max-h-48 w-auto rounded border border-edge bg-ink-850 object-contain"
        />
      </a>
    )
  }
  return (
    <audio
      controls
      src={url}
      preload="metadata"
      onError={() => setFailed(true)}
      className="w-full max-w-sm"
    />
  )
}

function TodoRow({
  block,
  index,
  onToggle,
}: {
  block: Extract<Block, { type: 'todo' }>
  index: number
  onToggle?: (todoIndex: number) => void
}) {
  return (
    <button
      type="button"
      disabled={!onToggle}
      onClick={() => onToggle?.(index)}
      className={`group flex w-full items-start gap-2.5 text-left text-sm ${
        onToggle ? 'cursor-pointer' : 'cursor-default'
      }`}
    >
      <span
        className={`mono mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-[3px] border text-[10px] ${
          block.done
            ? 'border-sync/40 bg-sync/10 text-sync'
            : 'border-edge-strong text-transparent group-hover:border-zinc-500'
        }`}
      >
        <Check size={11} />
      </span>
      <span className={block.done ? 'text-zinc-500 line-through decoration-zinc-600' : 'text-zinc-200'}>
        {block.text}
      </span>
    </button>
  )
}

function BlockView({
  block,
  todoIndex,
  onToggleTodo,
}: {
  block: Block
  todoIndex: number
  onToggleTodo?: (todoIndex: number) => void
}) {
  switch (block.type) {
    case 'heading': {
      const cls =
        block.level === 1
          ? 'text-base font-semibold tracking-tight text-zinc-50 border-b border-edge pb-1.5'
          : block.level === 2
            ? 'text-sm font-semibold tracking-tight text-zinc-100'
            : 'mono text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-400'
      return <p className={cls}>{block.text}</p>
    }
    case 'paragraph':
      return <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-zinc-300">{block.text}</p>
    case 'todo':
      return <TodoRow block={block} index={todoIndex} onToggle={onToggleTodo} />
    case 'bench':
      return (
        <div className="mono flex items-baseline justify-between gap-3 rounded border border-edge bg-ink-850 px-2.5 py-1.5 text-[12px]">
          <span className="truncate text-zinc-400">{block.label}</span>
          <span className="shrink-0">
            <span className="font-semibold text-sync">{block.value}</span>
            {block.unit ? <span className="ml-1 text-zinc-500">{block.unit}</span> : null}
          </span>
        </div>
      )
    case 'attachment':
      return <AttachmentView block={block} />
  }
}

/** Editorial AST renderer — the same blocks the server derives from content_raw. */
export function NoteViewer({ contentRaw, onToggleTodo, compact }: NoteViewerProps) {
  const blocks = parseBlocks(contentRaw)
  if (blocks.length === 0) {
    return <p className="text-[13px] italic text-zinc-600">Empty note</p>
  }
  let nextTodo = 0
  return (
    <div className={`flex flex-col ${compact ? 'gap-1.5' : 'gap-2.5'}`}>
      {blocks.map((block, i) => {
        const todoIndex = block.type === 'todo' ? nextTodo++ : -1
        return <BlockView key={i} block={block} todoIndex={todoIndex} onToggleTodo={onToggleTodo} />
      })}
    </div>
  )
}
